// Small, shared labels for tiered scoring teams — kept out of every component that needs
// to say "Two best balls · gross" so the console settings, the draft panel, the event page
// and the public board can never spell the same setting two different ways.

export type TeamRule = 'best1' | 'best2' | 'aggregate';
export type TeamBasis = 'gross' | 'net';

export const TEAM_RULE_LABELS: Record<TeamRule, string> = {
  best2: 'Two best balls',
  best1: 'One best ball',
  aggregate: 'Everyone counts',
};

export const TEAM_BASIS_LABELS: Record<TeamBasis, string> = {
  gross: 'Gross',
  net: 'Net',
};

// ── Team mode ────────────────────────────────────────────────────────────────
// Whether the tiered teams that score this event are a second roster cut across the
// walking groups (SEPARATE — the long-standing behaviour) or the walking groups
// themselves (SAME — a scramble or a big outing where everyone plays with their team).
// One place for the label and the one-sentence copy so the "How the day runs" select, its
// help text and the event-page header chip can never drift apart.

export type TeamMode = 'separate' | 'same';

export const TEAM_MODE_LABELS: Record<TeamMode, string> = {
  separate: 'Separate from the foursomes',
  same: 'The foursomes themselves',
};

export const TEAM_MODE_HELP: Record<TeamMode, string> = {
  separate:
    'Tiered teams are drawn by handicap across every group, with a cash table alongside them once this event shows money.',
  same: 'Each foursome scores as its own team.',
};

/** The sentence a cash table puts under its own heading — said once, so nobody adds up a
 *  team's figure expecting it to be one shared game rather than four separate ones. */
export const TEAM_CASH_HEADER = "Sum of each player’s net across their foursome’s games";

/** 1 -> A, 2 -> B, ... A player with no tier (no handicap on file) reads as a dash. */
export function tierLetter(tier: number | null | undefined): string {
  if (!tier || !Number.isFinite(tier) || tier < 1) return '—';
  return String.fromCharCode(64 + Math.round(tier));
}

/** "Two best balls · gross" — the sentence a board or panel puts under the Teams heading. */
export function teamRuleLine(
  rule: TeamRule | null | undefined,
  basis: TeamBasis | null | undefined
): string {
  const r = rule && TEAM_RULE_LABELS[rule] ? rule : 'best2';
  const b = basis && TEAM_BASIS_LABELS[basis] ? basis : 'gross';
  return `${TEAM_RULE_LABELS[r]} · ${TEAM_BASIS_LABELS[b]}`;
}
